import React from 'react'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'
import loadable from '@loadable/component'
import loadAuthor from './loadAuthor.gql'
import { venture, imagesUrl, enableContentful as enableContentfulFromConfig } from '../../config'
import UnicornNavigationMenu from '../UnicornNavigationMenu'
import Footer from '../Footer/index'
import { styles } from './theme'
import NotFound from '../NotFound'
import { graphql } from 'react-apollo'
import { StyledApp, StyledAuthorImage, StyledAuthorName, StyledAuthorDescription, StyledHr, StyledContainer, StyledNoBlogPost, StyledContentfulAuthorDescription } from './styles'
import LoadingPage from '../../components/pages/Loading'
const BlogGrid = loadable(() => import('@albatross/common-components/dist/components/Blogs/Grid'))
const DynamicHeader = loadable(() => import('@albatross/common-components/dist/components/DynamicHeader'))
const NavigationBreadcrumb = loadable(() => import('@albatross/common-components/dist/components/NavigationBreadcrumb'))
const HTMLHeaderElements = loadable(() => import('@albatross/common-components/dist/components/MetaTags'))

const AuthorPage = props => {
  const ventureStyles = styles[venture] ? styles[venture] : styles.default
  const { data } = props
  const {
    globalContent,
    globalComponents,
    loading,
    author,
    contentfulAuthor,
    contentfulHeader,
    contentfulGlobalSettings,
    games,
    variables: { enableContentful }
  } = data || {}
  const location = useLocation()

  if (loading) {
    return <LoadingPage />
  }

  let contentfulGlobalComponents = {}
  let contentfulGlobalContent = { ...contentfulGlobalSettings }

  if (contentfulHeader) {
    contentfulGlobalComponents = {
      components: {
        DynamicHeader: {
          globalConfig: {
            ...contentfulHeader.header,
            ...(contentfulGlobalSettings?.searchIcon
              ? { searchIcon: contentfulGlobalSettings.searchIcon }
              : {})
          }
        }
      }
    }
    contentfulGlobalContent = {
      menuItems: contentfulHeader.menuItems,
      dropdownMenu: contentfulHeader.dropdownMenu,
      ...contentfulGlobalContent
    }
  }

  const commonProps = {
    globalContent: enableContentful ? contentfulGlobalContent : globalContent,
    globalComponents: enableContentful ? contentfulGlobalComponents : globalComponents
  }

  const currentAuthor = enableContentful ? contentfulAuthor : author

  if (!currentAuthor) {
    return <NotFound {...commonProps} />
  }

  const blogs = enableContentful ? [...contentfulAuthor?.blogs] : [...games]
  const authorImageUrl = !enableContentful ? `${imagesUrl}${currentAuthor?.image}` : contentfulAuthor?.image

  const renderDescription = () => {
    if (!currentAuthor?.description) {
      return null
    }

    if (!enableContentful) {
      return <StyledAuthorDescription>{currentAuthor.description}</StyledAuthorDescription>
    }

    return <StyledContentfulAuthorDescription venture={venture} dangerouslySetInnerHTML={{ __html: currentAuthor?.description }}/>
  }

  return (
    <>
      <HTMLHeaderElements
        title={currentAuthor.name}
        currentPathname={location.pathname}
      />
      <DynamicHeader
        venture={venture}
        globalConfig={commonProps.globalComponents?.components?.DynamicHeader?.globalConfig}
        categories={commonProps.globalContent?.menuItems}
        appLink={commonProps.globalContent?.appLinks}
        enableContentful={enableContentful}
      />
      <UnicornNavigationMenu globalContent={commonProps.globalContent} venture={venture} />

      <StyledApp
        id="styledApp"
        styles={ventureStyles}
        isNavMenuPresent
      >
        <NavigationBreadcrumb isBlogPage showBreadcrumb={commonProps.globalContent.showBreadcrumb} venture={venture} />
        <StyledContainer authorDescription={currentAuthor.description}>
          {currentAuthor.image ? <StyledAuthorImage src={authorImageUrl} /> : null}
          <div>
            <StyledAuthorName venture={venture}>{currentAuthor.name}</StyledAuthorName>
            {renderDescription()}
          </div>
        </StyledContainer>
        <StyledHr />
        {blogs.length > 0
          ? <BlogGrid
            blogList={blogs}
            headingText={`Posts by ${currentAuthor.name}`}
            venture={venture}
            defaultAuthorImage="/uk/jackpotjoy/AuthorFallback.png"
            enableContentful={enableContentful}
          />
          : <StyledNoBlogPost>
            No Blogs Available
          </StyledNoBlogPost>
        }
        <Footer enableContentful={enableContentful} venture={venture} />
      </StyledApp>
    </>
  )
}

const AuthorPageWithData = graphql(loadAuthor, {
  options: (props) => {
    const { location, match } = props
    const { search } = location
    const { params } = match

    const authorId = params?.id
    const urlParams = new URLSearchParams(search)
    const enableContentfulOverride = urlParams.get('enableContentful') === 'true'
    const enableContentful = urlParams.get('enableContentful') !== null ? enableContentfulOverride : enableContentfulFromConfig

    const variables = {
      id: authorId,
      enableContentful
    }
    return {
      variables
    }
  }
})(AuthorPage)

AuthorPage.propTypes = {
  history: PropTypes.object,
  data: PropTypes.shape({
    globalContent: PropTypes.object,
    globalComponents: PropTypes.object,
    loading: PropTypes.bool,
    author: PropTypes.object,
    contentfulAuthor: PropTypes.object,
    contentfulHeader: PropTypes.object,
    contentfulGlobalSettings: PropTypes.object,
    games: PropTypes.array
  })
}

export default AuthorPageWithData
